import AddIcon from "@mui/icons-material/Add";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import { Box, Button, Card, CardContent, Chip, Dialog, DialogContent, IconButton, TextField, Tooltip } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PanelHeader } from "../components/Panel";
import { financeApi } from "../lib/api";
import { copyText } from "../lib/clipboard";
import { queryKeys } from "../lib/queryKeys";
import type { McpToken, McpTokenCreated, McpTokenScope } from "../lib/types";

const mcpServerUrl = `${window.location.origin}/mcp`;
const claudeMcpAddCommand = `claude mcp add --transport http finance ${mcpServerUrl} \\\n  --header "Authorization: Bearer <token>"`;

const dateFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
const dateTimeFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });

function scopeLabel(scope: McpTokenScope) {
  return scope === "read_write" ? "Read & write" : "Read only";
}

function lastUsedLabel(token: McpToken) {
  if (!token.last_used_at) return "Never used";
  const parsed = new Date(token.last_used_at);
  if (Number.isNaN(parsed.getTime())) return "Never used";
  return `Last used ${dateTimeFormatter.format(parsed)}`;
}

function createdLabel(token: McpToken) {
  const parsed = new Date(token.created_at);
  if (Number.isNaN(parsed.getTime())) return "";
  return `Created ${dateFormatter.format(parsed)}`;
}

function CopyCodeBlock({ code, copyLabel }: { code: string; copyLabel: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    const ok = await copyText(code);
    setCopied(ok);
    if (ok) window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mcp-code-block">
      <pre>{code}</pre>
      <Tooltip title={copied ? "Copied!" : copyLabel}>
        <IconButton className="records-icon-button" type="button" aria-label={copyLabel} onClick={() => void copy()}>
          <ContentCopyOutlinedIcon fontSize="small" aria-hidden="true" />
        </IconButton>
      </Tooltip>
    </div>
  );
}

export function McpTokensPage() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<McpToken | null>(null);
  const [revokeError, setRevokeError] = useState("");

  const tokensQuery = useQuery({ queryKey: queryKeys.mcpTokens, queryFn: financeApi.mcpTokens });
  const tokens = tokensQuery.data ?? [];

  const revokeMutation = useMutation({
    mutationFn: (tokenId: string) => financeApi.revokeMcpToken(tokenId),
    onSuccess: async () => {
      setRevokeTarget(null);
      setRevokeError("");
      await queryClient.invalidateQueries({ queryKey: queryKeys.mcpTokens });
    },
    onError: (error) => {
      setRevokeError(error instanceof Error ? error.message : "Could not revoke token");
    },
  });

  return (
    <>
      <header className="topbar">
        <Box>
          <h1>MCP access (Beta)</h1>
          <p>Connect AI agents — Claude, Cursor, and other MCP clients — to your finance data.</p>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} type="button" onClick={() => setCreateOpen(true)}>
          Create token
        </Button>
      </header>

      <section className="content mcp-grid">
        <Card className="records-panel">
          <CardContent className="records-panel-content">
            <Box className="settings-overview-card-header">
              <div>
                <h2>Tokens</h2>
                <p>Tokens authenticate MCP clients as you. Revoking a token cuts its access immediately.</p>
              </div>
            </Box>
            {tokens.length ? (
              <div className="mcp-token-list">
                {tokens.map((token) => (
                  <article className="mcp-token-row" key={token.id}>
                    <span className="mcp-token-icon">
                      <KeyOutlinedIcon fontSize="small" aria-hidden="true" />
                    </span>
                    <div className="mcp-token-main">
                      <div className="mcp-token-title">
                        <strong>{token.name}</strong>
                        <Chip
                          className="chip"
                          size="small"
                          label={scopeLabel(token.scope)}
                          color={token.scope === "read_write" ? "warning" : "default"}
                        />
                      </div>
                      <code>{token.token_prefix}…</code>
                      <span className="mcp-token-meta">
                        {createdLabel(token)} · {lastUsedLabel(token)}
                      </span>
                    </div>
                    <Tooltip title="Revoke token">
                      <IconButton
                        className="records-icon-button danger"
                        type="button"
                        aria-label={`Revoke token ${token.name}`}
                        onClick={() => {
                          setRevokeError("");
                          setRevokeTarget(token);
                        }}
                      >
                        <DeleteOutlineIcon aria-hidden="true" />
                      </IconButton>
                    </Tooltip>
                  </article>
                ))}
              </div>
            ) : !tokensQuery.isLoading ? (
              <div className="mcp-empty">
                <SmartToyOutlinedIcon aria-hidden="true" />
                <p>No tokens yet. Create one to let an AI agent read your finance data.</p>
                <Button variant="outlined" startIcon={<AddIcon />} type="button" onClick={() => setCreateOpen(true)}>
                  Create token
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="records-panel">
          <CardContent className="records-panel-content">
            <Box className="settings-overview-card-header">
              <div>
                <h2>How to connect</h2>
                <p>The MCP server speaks Streamable HTTP and works with any MCP client.</p>
              </div>
            </Box>
            <ol className="mcp-steps">
              <li>
                <div className="mcp-step-body">
                  <p>Create a token above and copy it — it is shown only once.</p>
                </div>
              </li>
              <li>
                <div className="mcp-step-body">
                  <p>Point your MCP client at the server URL:</p>
                  <CopyCodeBlock code={mcpServerUrl} copyLabel="Copy server URL" />
                  <p className="mcp-step-hint">
                    Send the token as an <code>Authorization: Bearer</code> header.
                  </p>
                </div>
              </li>
              <li>
                <div className="mcp-step-body">
                  <p>For Claude Code, one command does both:</p>
                  <CopyCodeBlock code={claudeMcpAddCommand} copyLabel="Copy Claude Code command" />
                  <p className="mcp-step-hint">
                    Cursor and VS Code take the same URL and header in their MCP config files.
                  </p>
                </div>
              </li>
            </ol>
            <div className="mcp-scope-note">
              <p>
                <strong>Read only</strong> tokens can list transactions, accounts, categories, and reports.
              </p>
              <p>
                <strong>Read &amp; write</strong> tokens can also create and edit transactions — deleting is never allowed over MCP.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {createOpen ? (
        <CreateTokenModal
          onClose={async (created) => {
            setCreateOpen(false);
            if (created) {
              await queryClient.invalidateQueries({ queryKey: queryKeys.mcpTokens });
            }
          }}
        />
      ) : null}

      {revokeTarget ? (
        <ConfirmDialog
          titleId="revokeMcpTokenTitle"
          title="Revoke token?"
          description={`Clients using "${revokeTarget.name}" will lose access immediately. This cannot be undone.`}
          confirmLabel="Revoke"
          closeAriaLabel="Close revoke dialog"
          pending={revokeMutation.isPending}
          error={revokeError}
          onConfirm={() => revokeMutation.mutate(revokeTarget.id)}
          onClose={() => setRevokeTarget(null)}
        />
      ) : null}
    </>
  );
}

function CreateTokenModal({ onClose }: { onClose: (created: boolean) => void }) {
  const [name, setName] = useState("");
  const [scope, setScope] = useState<McpTokenScope>("read");
  const [created, setCreated] = useState<McpTokenCreated | null>(null);
  const [status, setStatus] = useState("");

  const createMutation = useMutation({
    mutationFn: () => financeApi.createMcpToken({ name, scope }),
    onSuccess: (token) => {
      setStatus("");
      setCreated(token);
    },
    onError: (error) => {
      setStatus(error instanceof Error ? error.message : "Could not create token");
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim()) {
      setStatus("Token name cannot be blank");
      return;
    }
    createMutation.mutate();
  };

  const close = () => onClose(created !== null);

  return (
    <Dialog open onClose={close} aria-labelledby="createMcpTokenTitle" slotProps={{ paper: { className: "modal records-modal users-group-modal" } }}>
      <DialogContent className="records-modal-content">
        <PanelHeader
          titleId="createMcpTokenTitle"
          title={created ? "Token created" : "Create MCP token"}
          subtitle={
            created
              ? "Copy the token now — it is shown only once and cannot be recovered later."
              : "The token grants access to your data in all your groups."
          }
          closeAriaLabel="Close token dialog"
          onClose={close}
        />

        {created ? (
          <div className="mcp-created-token">
            <CopyCodeBlock code={created.token} copyLabel="Copy token" />
            <div className="records-modal-actions">
              <Button variant="contained" type="button" onClick={close}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form className="users-group-invite-create" onSubmit={submit}>
            <TextField
              label="Token name"
              value={name}
              placeholder="e.g. Claude on my laptop"
              slotProps={{ htmlInput: { maxLength: 120 } }}
              onChange={(event) => setName(event.target.value)}
            />
            <TextField
              select
              label="Scope"
              value={scope}
              slotProps={{ select: { native: true } }}
              onChange={(event) => setScope(event.target.value as McpTokenScope)}
            >
              <option value="read">Read only</option>
              <option value="read_write">Read &amp; write</option>
            </TextField>
            <Button variant="contained" startIcon={<AddIcon />} type="submit" disabled={createMutation.isPending}>
              Create token
            </Button>
          </form>
        )}
        {status ? <p className="records-error">{status}</p> : null}
      </DialogContent>
    </Dialog>
  );
}
